"use client";

import { useEffect, useRef, useState } from "react";
import { MAP_THEME, MapStyleKey } from "./mapTheme";

interface SpiderFeature {
  id: string;
  baseTileUrl: string;
  thumbnailUrl?: string;
  name?: string;
  width?: number;
  height?: number;
  poseHeading?: number;
  posePitch?: number;
  poseRoll?: number;
  averageRating?: number;
  viewCount?: number;
  creatorName?: string;
  isVerifiedCreator?: boolean;
  ownerId?: string;
  defaultYaw?: number;
  defaultPitch?: number;
  defaultZoom?: number;
}

interface SpiderOverlayProps {
  features: SpiderFeature[];
  screenX: number;
  screenY: number;
  mapStyle: MapStyleKey;
  onSelect: (f: SpiderFeature) => void;
  onClose: () => void;
}

const LEG_LENGTH = 84;
const MARKER_R = 36;

function getPositions(count: number, cx: number, cy: number) {
  if (count === 1) return [{ x: cx, y: cy - LEG_LENGTH }];
  return Array.from({ length: count }, (_, i) => {
    const angle = (2 * Math.PI * i) / count - Math.PI / 2;
    return {
      x: cx + LEG_LENGTH * Math.cos(angle),
      y: cy + LEG_LENGTH * Math.sin(angle),
    };
  });
}

export default function SpiderOverlay({
  features,
  screenX,
  screenY,
  mapStyle,
  onSelect,
  onClose,
}: SpiderOverlayProps) {
  const t_ = MAP_THEME[mapStyle];
  const [visible, setVisible] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (overlayRef.current && !overlayRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const positions = getPositions(features.length, screenX, screenY);

  return (
    <div ref={overlayRef} className="absolute inset-0 z-[50] pointer-events-none">
      {/* SVG spider legs */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        {positions.map((pos, i) => (
          <line
            key={i}
            x1={screenX}
            y1={screenY}
            x2={visible ? pos.x : screenX}
            y2={visible ? pos.y : screenY}
            stroke={t_.spiderLeg}
            strokeWidth={1.5}
            strokeDasharray="4 3"
            style={{ transition: `x2 0.35s cubic-bezier(.4,0,.2,1) ${i * 30}ms, y2 0.35s cubic-bezier(.4,0,.2,1) ${i * 30}ms` }}
          />
        ))}
        {/* Origin dot */}
        <circle cx={screenX} cy={screenY} r={5} fill={t_.spiderDot} />
      </svg>

      {/* Spider markers */}
      {positions.map((pos, i) => {
        const f = features[i];
        const style: React.CSSProperties = {
          position: "absolute",
          left: visible ? pos.x - MARKER_R : screenX - MARKER_R,
          top: visible ? pos.y - MARKER_R : screenY - MARKER_R,
          transition: `left 0.35s cubic-bezier(.4,0,.2,1) ${i * 30}ms, top 0.35s cubic-bezier(.4,0,.2,1) ${i * 30}ms, opacity 0.2s ${i * 30}ms`,
          opacity: visible ? 1 : 0,
          width: MARKER_R * 2,
          height: MARKER_R * 2,
          pointerEvents: "auto",
        };

        return (
          <button
            key={f.id}
            style={style}
            onClick={() => onSelect(f)}
            className="group flex flex-col items-center"
            title={f.name || "Open panorama"}
          >
            {/* Thumbnail card or fallback dot */}
            <div className="relative flex flex-col items-center">
              <div
                className="overflow-hidden shadow-xl transition-transform duration-150 group-hover:scale-110"
                style={{
                  width: 64,
                  height: 36,
                  borderRadius: 7,
                  border: `2px solid ${t_.cardBorder}`,
                  background: f.thumbnailUrl ? t_.cardBg : t_.fallback,
                  boxShadow: `0 0 0 1px ${t_.ring}`,
                }}
              >
                {f.thumbnailUrl ? (
                  <img
                    src={f.thumbnailUrl}
                    width={64}
                    height={36}
                    draggable={false}
                    style={{ objectFit: "cover", width: "100%", height: "100%", display: "block" }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-ink text-[9px] font-bold tracking-wider opacity-70">
                    360°
                  </div>
                )}
              </div>
              {/* Pointer */}
              <div style={{ width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: `5px solid ${t_.cardBorder}`, marginTop: -1 }} />
            </div>
            {/* Hover tooltip */}
            <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity z-10 w-max">
              {f.name && (
                <span className="whitespace-nowrap text-[10px] font-semibold text-ink bg-tone-900/90 border border-ink/10 px-2 py-0.5 rounded-md shadow-lg block text-center">
                  {f.name}
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
